import { ModelingLanguage } from "@kea-mod/common";
import {
  KEAGraph,
  keaNamespace,
  KEAPaper,
  KEAPaperBuilder,
  hasElementVariants,
  isAppendable,
  linkVariantsFromTemplate,
  LinkLabelPosition,
  PaperEventTypes,
} from "@kea-mod/jointjs";
import { InteractionType, KEAGraphContext } from "context/KEAGraphContext";
import { Component, ContextType, createRef, ReactNode, RefObject } from "react";
import { WithTranslation, withTranslation } from "react-i18next";
import { getLanguageMarkup } from "./misc/GetLanguageMarkup";
import { findNotation, PaletteNotation, paletteNotations } from "./PaletteSections";
import { ResizableCard } from "./ResizbleCard";

interface State {
  selected: ModelingLanguage;
  openNotation: string | null;
  openGroup: ModelingLanguage | null;
}

interface Props extends WithTranslation {
  modelLanguage: ModelingLanguage;
  setModelLanguage: (language: ModelingLanguage) => void;
  userCanChangeModelingLanguage: boolean;
  positionX: number;
  positionY: number;
  width: number;
  height: number;
  setPositionX: (positionX: number) => void;
  setPositionY: (positionY: number) => void;
  setWidth: (width: number) => void;
  setHeight: (height: number) => void;
}

const MIN_SCALE = 0.15;
const MAX_SCALE = 1;

const paperOptions: joint.dia.Paper.Options = {
  width: "100%",
  height: 200,
  interactive: false,
  gridSize: 5,
  cellViewNamespace: keaNamespace,
  background: {
    color: "rgb(245, 245, 245, .3)",
  },
};

export class SecondaryGraph extends Component<Props, State> {
  static contextType = KEAGraphContext;
  context!: ContextType<typeof KEAGraphContext>;

  secondaryGraphRef: RefObject<HTMLDivElement>;
  sectionsRef: RefObject<HTMLDivElement>;

  flyGraph: KEAGraph = new KEAGraph({}, { cellNamespace: keaNamespace });
  flyPaper: KEAPaper | undefined = undefined;
  ownTemplatePaper: KEAPaper | undefined = undefined;
  observedWidth = 0;
  resizeObserver: ResizeObserver | undefined = undefined;

  constructor(props: Props) {
    super(props);
    this.state = {
      selected: this.props.modelLanguage,
      openNotation: findNotation(this.props.modelLanguage)?.key ?? null,
      openGroup: findNotation(this.props.modelLanguage) ? this.props.modelLanguage : null,
    };
    this.secondaryGraphRef = createRef();
    this.sectionsRef = createRef();
  }

  /**
   * The rendered size of the elements plus the same margin on the right and bottom as on the left
   * and top. Measured against the SVG itself, not the viewport, because the element container moves
   * with the opened section and its viewport position is therefore not a margin.
   */
  private renderedSize(svg: SVGSVGElement): { width: number; height: number } {
    const topGroup: NodeListOf<SVGGElement> = svg.querySelectorAll("svg > g");
    const origin = svg.getBoundingClientRect();
    const bboxes = Array.from(topGroup).map((group) => group.getBoundingClientRect());

    return {
      width: bboxes.reduce((acc, bbox) => Math.max(acc, 2 * (bbox.x - origin.x) + bbox.width), 0),
      height: bboxes.reduce((acc, bbox) => Math.max(acc, 2 * (bbox.y - origin.y) + bbox.height), 0),
    };
  }

  /**
   * Scale the elements to the width of the card. The positions come from the markup and cannot
   * reflow, so the scale is the only way to follow a resize. The new scale is the current one times
   * the ratio of available to rendered width.
   */
  private fitToCardWidth() {
    const container = this.secondaryGraphRef.current;
    const svg = container?.querySelector("svg") as SVGSVGElement | null;
    if (container == null || svg == null || container.clientWidth === 0) return;

    const current = this.templatePaper().scale().sx;
    const rendered = this.renderedSize(svg);
    if (current === 0 || rendered.width === 0) return;

    const scale = Math.min(Math.max((current * container.clientWidth) / rendered.width, MIN_SCALE), MAX_SCALE);
    this.templatePaper().scale(scale, scale);

    const fitted = this.renderedSize(svg);
    svg.setAttribute("width", fitted.width.toString());
    svg.setAttribute("height", fitted.height.toString());
    container.style.height = `${fitted.height}px`;
  }

  // The sub-palettes show a section of the same notation, but their edge types only exist in the full palette.
  private static FULL_PALETTE: Partial<Record<ModelingLanguage, ModelingLanguage>> = {
    [ModelingLanguage.BPMN_EVENT]: ModelingLanguage.BPMN,
    [ModelingLanguage.BPMN_GATEWAY]: ModelingLanguage.BPMN,
    [ModelingLanguage.BPMN_ACTIVITY]: ModelingLanguage.BPMN,
    [ModelingLanguage.BPMN_SWIMLANE]: ModelingLanguage.BPMN,
  };

  private variantSource(language: ModelingLanguage): KEAGraph {
    const full = SecondaryGraph.FULL_PALETTE[language];
    if (!full) return this.context.templateGraph;
    const graph = new KEAGraph({}, { cellNamespace: keaNamespace });
    graph.fromJSON(getLanguageMarkup(full).graph);
    return graph;
  }

  /**
   * All templates of the notation, for BPMN across the sub-palettes; switchable types appear once.
   */
  private appendTemplates(language: ModelingLanguage): joint.dia.Element[] {
    const languages = findNotation(language)?.groups.map((group) => group.language) ?? [language];
    const seen = new Set<string>();
    const templates: joint.dia.Element[] = [];
    languages.forEach((each) => {
      const graph = new KEAGraph({}, { cellNamespace: keaNamespace });
      graph.fromJSON(getLanguageMarkup(each).graph);
      graph.getElements().forEach((element) => {
        const type = element.get("type");
        const size = element.size();
        if (element.get("parent") || !isAppendable(type) || !size.width || !size.height) return;
        if (hasElementVariants(type)) {
          if (seen.has(type)) return;
          seen.add(type);
          // The base shape belongs here, not whichever variant comes first in the palette.
          const Constructor = (keaNamespace as any).kea?.[type.replace("kea.", "")];
          if (Constructor) {
            templates.push(new Constructor());
            return;
          }
        }
        templates.push(element);
      });
    });
    return templates;
  }

  private applyLanguage(language: ModelingLanguage) {
    const configuration = getLanguageMarkup(language);

    this.context.templateGraph.fromJSON(configuration.graph);
    this.templatePaper().scale(configuration.scaleFactor, configuration.scaleFactor);
    this.context.modelingPaper.options.defaultLink = configuration.defaultLink;
    // Edge types are notation specific, so the remembered one no longer applies after a switch.
    this.context.modelingPaper.lastLinkVariant = undefined;
    this.context.modelingPaper.linkVariants = linkVariantsFromTemplate(this.variantSource(language).getLinks());
    this.context.modelingPaper.appendTemplates = this.appendTemplates(language);
    this.context.modelingPaper.linkLabelPositions = configuration.linkConfiguration.addLabelEnabled
      ? [LinkLabelPosition.SOURCE, LinkLabelPosition.MID, LinkLabelPosition.TARGET]
      : [];
    this.context.updateLinkConfiguration(configuration.linkConfiguration);

    this.fitToCardWidth();
  }

  componentDidMount = () => {
    //this is a hacky, but works. Joint elements need to be in DOM to import new graphs. Otherwise, the scaling goes wrong. There is no way to guarantee, that the virtualDOM is indeed present in the actual DOM.
    //look at https://stackoverflow.com/questions/26556436/react-after-render-code
    setTimeout(() => {
      this.ownTemplatePaper = this.getPaper();
      this.context.templatePaper = this.ownTemplatePaper;
    }, 1000);
    setTimeout(() => {
      this.applyLanguage(this.props.modelLanguage);
      this.observeCardWidth();
    }, 2000);
  };

  componentWillUnmount = () => {
    this.resizeObserver?.disconnect();
  };

  /**
   * Only the width is watched. Reacting to the height as well would loop, because fitting the
   * palette sets the height of the element container itself.
   */
  private observeCardWidth() {
    const sections = this.sectionsRef.current;
    if (sections === null || typeof ResizeObserver === "undefined") return;

    this.observedWidth = sections.clientWidth;
    this.resizeObserver = new ResizeObserver((entries) => {
      const width = entries[0].contentRect.width;
      if (Math.abs(width - this.observedWidth) < 1) return;
      this.observedWidth = width;
      this.fitToCardWidth();
    });
    this.resizeObserver.observe(sections);
  }

  componentDidUpdate = (prevProps: Props, prevState: State) => {
    if (this.props.modelLanguage !== prevProps.modelLanguage) {
      this.applyLanguage(this.props.modelLanguage);
      this.setState({
        selected: this.props.modelLanguage,
        openNotation: findNotation(this.props.modelLanguage)?.key ?? null,
        openGroup: findNotation(this.props.modelLanguage) ? this.props.modelLanguage : null,
      });
    }
    if (this.state.selected !== prevState.selected) {
      this.applyLanguage(this.state.selected);
    }
    // Measure once the container is visible again, its height is zero while it is hidden.
    if (this.state.openGroup !== null && prevState.openGroup === null) {
      this.fitToCardWidth();
    }
  };

  /**
   * The provider keeps the template paper in React state, but componentDidMount assigns it on the
   * context value directly. That assignment is lost on the next provider render, which then hands
   * out the untouched initial paper again - one that was never attached to the DOM. Keep our own
   * reference so later calls reach the paper that is actually rendered.
   */
  templatePaper = (): KEAPaper => this.ownTemplatePaper ?? this.context.templatePaper;

  getPaper = (): KEAPaper => {
    return new KEAPaperBuilder(this.secondaryGraphRef, this.context.templateGraph, paperOptions)
      .historyClb(this.historyClb)
      .interactionClb(this.interactionClb)
      .dropGraph(this.flyGraph)
      .dropPaper(this.context.modelingPaper)
      .event(PaperEventTypes.NODE_DRAG_AND_DROP)
      .build();
  };

  historyClb = () => {
    this.context.pushPresent();
  };

  interactionClb = (
    interactionType: InteractionType,
    interactionTime: number,
    payload?: KEAGraph | joint.dia.Link | joint.dia.Cell | joint.dia.Cell[],
  ) => {
    this.context.addUserInteraction(interactionType, interactionTime, payload);
  };

  selectLanguage = (language: ModelingLanguage) => {
    if (language === this.state.selected) return;
    this.props.setModelLanguage(language);
    this.setState({ selected: language });
  };

  toggleNotation = (notation: PaletteNotation) => {
    if (this.state.openNotation === notation.key) {
      this.setState({ openNotation: null, openGroup: null });
      return;
    }
    if (notation.groups.length === 1) {
      this.setState({ openNotation: notation.key, openGroup: notation.groups[0].language });
      this.selectLanguage(notation.groups[0].language);
      return;
    }
    this.setState({ openNotation: notation.key, openGroup: null });
  };

  toggleGroup = (language: ModelingLanguage) => {
    if (this.state.openGroup === language) {
      this.setState({ openGroup: null });
      return;
    }
    this.setState({ openGroup: language });
    this.selectLanguage(language);
  };

  isNotationAvailable = (notation: PaletteNotation): boolean => {
    return (
      this.props.userCanChangeModelingLanguage ||
      notation.groups.some((group) => group.language === this.props.modelLanguage)
    );
  };

  /**
   * The palette is rendered as one flat list so that the element container always keeps the same
   * parent. React then moves that node between the sections instead of recreating it, which would
   * destroy the JointJS paper attached to it.
   */
  renderSections = (): ReactNode[] => {
    const rows: ReactNode[] = [];
    const container = (
      <div
        key="elements"
        id={"graph_element_container"}
        className="palette-elements"
        ref={this.secondaryGraphRef}
        style={this.state.openGroup === null ? { display: "none" } : undefined}
      />
    );
    let containerPlaced = false;

    paletteNotations.forEach((notation) => {
      const isOpen = this.state.openNotation === notation.key;
      const available = this.isNotationAvailable(notation);
      const hasSubgroups = notation.groups.length > 1;

      rows.push(
        <button
          key={notation.key}
          type="button"
          className={`palette-row palette-notation${isOpen ? " is-open" : ""}`}
          disabled={!available}
          onClick={() => this.toggleNotation(notation)}
        >
          <span className="palette-arrow">{isOpen ? "▾" : "▸"}</span>
          <span className="palette-label">{this.props.t(notation.labelKey)}</span>
        </button>,
      );

      if (!isOpen) return;

      if (!hasSubgroups) {
        if (this.state.openGroup !== null) {
          rows.push(container);
          containerPlaced = true;
        }
        return;
      }

      notation.groups.forEach((group) => {
        const isActive = group.language === this.state.openGroup;
        rows.push(
          <button
            key={group.language}
            type="button"
            className={`palette-row palette-group${isActive ? " is-active" : ""}`}
            onClick={() => this.toggleGroup(group.language)}
          >
            <span className="palette-arrow">{isActive ? "▾" : "▸"}</span>
            <span className="palette-label">{this.props.t(group.labelKey)}</span>
          </button>,
        );
        if (isActive) {
          rows.push(container);
          containerPlaced = true;
        }
      });
    });

    if (!containerPlaced) rows.push(container);
    return rows;
  };

  render() {
    return (
      <ResizableCard
        positionX={this.props.positionX}
        positionY={this.props.positionY}
        width={this.props.width}
        height={this.props.height}
        minWidth={200}
        minHeight={400}
        visible={true}
        setPositionX={this.props.setPositionX}
        setPositionY={this.props.setPositionY}
        setWidth={this.props.setWidth}
        setHeight={this.props.setHeight}
        header={<></>}
        body={
          <div className="palette-sections" ref={this.sectionsRef}>
            {this.renderSections()}
          </div>
        }
      />
    );
  }
}

export default withTranslation()(SecondaryGraph);
